import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {Router} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';
import {ClrDatagridStateInterface} from '@clr/angular';
import {CurrentUser} from '../../../user/current-user.service';
import {TranslatorService} from '../../../translation/translator.service';
import {MasterService} from '../../master.service';
import {Customer} from '../../my-customers/types/customer';
import {Group} from '../../my-customers/types/group-type';
import {Test} from '../../../test/state/test';
import {TestQuery} from '../../../test/state/test.query';
import {TestFunctions} from '../../../test/state/test.functions';

@Component({
  selector: 'app-comparison',
  templateUrl: './comparison.component.html',
  styleUrls: ['./comparison.component.scss']
})
export class ComparisonComponent implements OnInit, OnDestroy {
  loading: boolean = true;
  customers: Customer[] = [];
  totalCustomerCount: number = 0;
  latestDatagridState: ClrDatagridStateInterface = {};
  searchInput: string = '';

  selectedUser1: Customer | null = null;
  selectedUser2: Customer | null = null;

  groups$: Observable<Group[]>;
  name: string;
  userId: string;
  masterHasTest = false;
  masterFirstCompletedTest: Test | null = null;
  private unsubscribe$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder,
              private masterService: MasterService,
              private currentUser: CurrentUser,
              private testQuery: TestQuery,
              private testFunctions: TestFunctions,
              private translatorService: TranslatorService,
              private router: Router) {
  }

  ngOnInit(): void {
    this.getMasterName();
    this.getUserId();
    this.loadGroups();

    this.latestDatagridState = {
      page: {
        size: 10,
        current: 1
      }
    };

    this.initializeCustomers();

    this.testQuery.selectTestsForCurrentUser()
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((tests: Test[]) => {
          const completedTests = tests.filter(test => this.testFunctions.isCompleted(test));
          const firstCompletedTest = completedTests.shift();
          if (!!firstCompletedTest) {
            this.masterHasTest = true;
            this.masterFirstCompletedTest = firstCompletedTest;
          }
        })
      ).subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initializeCustomers(): void {
    this.masterService.isLoading()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(isLoading => {
        this.loading = isLoading;
      });

    this.masterService.getCustomers()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(customers => {
        this.customers = customers;
      });

    this.masterService.getTotalNumbersOfCustomers()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(totalCustomerNumber => {
        this.totalCustomerCount = totalCustomerNumber;
      });

    this.refresh({});
  }

  refresh(state: ClrDatagridStateInterface): void {
    // Initialize state if empty
    if (!state.page) {
      state.page = {size: 10, current: 1};
    }
    const pageNumber = state.page.size;
    const currentPage = state.page.current;
    let sortBy = typeof state.sort?.by === 'string' ? state.sort?.by : 'DATE';
    if (sortBy === 'name') {
      sortBy = null;
    }
    let sortDir: string;
    if (sortBy === 'DATE' || sortBy === 'PRODUCT') {
      sortDir = state.sort?.reverse ? 'asc' : 'desc';
    }
    else {
      sortDir = state.sort?.reverse ? 'desc' : 'asc';
    }
    const searchValue = this.searchInput.length >= 3 ? this.searchInput : null;

    this.masterService.loadCustomersPaginated(currentPage, pageNumber, searchValue, sortBy, sortDir);
    this.latestDatagridState = state;
  }

  getMasterName(): void {
    this.name = this.currentUser.fullName;
  }

  getUserId(): void {
    this.userId = this.currentUser.keycloakId;
  }

  loadGroups(): void {
    this.groups$ = this.masterService.loadGroups();
  }

  searchCustomers(): void {
    if (this.searchInput.length >= 3 || this.searchInput.length === 0) {
      if (this.latestDatagridState?.page) {
        this.latestDatagridState.page.current = 1; // Reset to first page on search
      }
      this.refresh(this.latestDatagridState || {});
    }
  }

  clearSearchInput(): void {
    if (this.searchInput.length > 0) {
      this.searchInput = '';
      this.searchCustomers();
    }
  }

  compare(): void {
    const user1TestId = this.selectedUser1?.getIdOfFirstFinishedTest();
    const user2TestId = this.selectedUser2?.getIdOfFirstFinishedTest();

    if (this.selectedUser1 && user1TestId && this.selectedUser2 && user2TestId) {
      this.router.navigateByUrl('/toolbox/comparer', {
        state: {
          user1Id: this.selectedUser1.keycloakId,
          user1TestId,
          user1Name: this.selectedUser1.fullName,
          user2Id: this.selectedUser2.keycloakId,
          user2TestId,
          user2Name: this.selectedUser2.fullName
        }
      });
    }
    else {
      console.error('Cannot compare: Missing selected user or test ID.');
    }
  }

  toggleSelection(customer: Customer): void {
    if (!customer.hasFinishedTests()) {
      return;
    }

    if (this.isSelected(customer)) {
      this.unselectCustomer(customer);
    }
    else if (this.canSelectMore()) {
      if (this.selectedUser1 === null) {
        this.selectedUser1 = customer;
      }
      else if (this.selectedUser2 === null) {
        this.selectedUser2 = customer;
      }
    }
  }

  canSelectMore(): boolean {
    return this.selectedUser1 === null || this.selectedUser2 === null;
  }

  unselectCustomer(customer: Customer): void {
    if (this.selectedUser1 && this.selectedUser1.keycloakId === customer.keycloakId) {
      this.selectedUser1 = null;
    }
    else if (this.selectedUser2 && this.selectedUser2.keycloakId === customer.keycloakId) {
      this.selectedUser2 = null;
    }
  }

  selectMaster(): void {
    if (this.masterFirstCompletedTest && !this.isMasterSelected()) {
      const masterCustomer = new Customer(
        this.userId,
        this.currentUser.firstName,
        this.currentUser.lastName,
        this.currentUser.email,
        '',
        [this.masterFirstCompletedTest],
        undefined, undefined, true
      );

      if (this.selectedUser1 === null) {
        this.selectedUser1 = masterCustomer;
      }
      else if (this.selectedUser2 === null) {
        this.selectedUser2 = masterCustomer;
      }
    }
  }

  bothSelected(): boolean {
    return this.selectedUser1 !== null && this.selectedUser2 !== null;
  }

  isSelected(customer: Customer): boolean {
    return (this.selectedUser1 !== null && this.selectedUser1.keycloakId === customer.keycloakId) ||
      (this.selectedUser2 !== null && this.selectedUser2.keycloakId === customer.keycloakId);
  }

  isMasterSelected(): boolean {
    return (this.selectedUser1 !== null && this.selectedUser1.keycloakId === this.userId) ||
      (this.selectedUser2 !== null && this.selectedUser2.keycloakId === this.userId);
  }

  __(key: string): string {
    return this.translatorService.translate('master.my-customers.' + key);
  }
}
